import axios from 'axios';

import { getCurrentEnvUrls } from '@/libs/config';

/**
 * Client for the OIDC IdP's interaction endpoints (guardian-view-be,
 * `/api/v1/sso/interaction/:uid/*`). This app is the interaction UI the provider redirects
 * the browser to; it drives login/consent then navigates to the returned resume URL.
 *
 * Uses a dedicated axios instance (not the shared US Kid Golf client) for two reasons the
 * IdP requires (see docs/tasks/015 on the BE):
 *  - `withCredentials` — the interaction/session cookie lives on the IdP origin
 *    (SameSite=None; Secure); without it the provider can't tie the call to the interaction.
 *  - `Accept: application/json` — makes the controller return `{ redirectTo }` for us to
 *    navigate to, instead of a cross-site 302 that XHR can't follow with the cookie.
 * These routes are `@Public` on the BE, so no Firebase bearer interceptor is needed.
 */

export type InteractionPrompt = 'login' | 'consent';

export type InteractionDetails = {
  uid: string;
  prompt: InteractionPrompt;
  params: { client_id?: string; scope?: string };
  /**
   * True when this consent needs the guardian's explicit approval to share their children
   * list (the `players` scope — child data). The client must render a consent UI and pass
   * the guardian's choice to `confirmInteraction` via `sharePlayers` (BE docs/tasks/019).
   */
  requiresPlayersConsent?: boolean;
};

export type InteractionResult = { redirectTo: string };

const client = axios.create({
  timeout: 15_000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

const interactionUrl = (uid: string): string => {
  // The OIDC IdP (guardian-view-be) is fronted by its own origin (`ssoIssuerUrl`), a subdomain
  // of the web app's domain (e.g. auth.hnl.works). It MUST be same-site with the web so the
  // interaction/session cookie isn't dropped by Safari's ITP as a third-party cookie — the
  // Cloud Run `*.run.app` URL is a separate site and breaks the cookie on Safari.
  const base = getCurrentEnvUrls().ssoIssuerUrl.replace(/\/+$/, '');
  return `${base}/api/v1/sso/interaction/${encodeURIComponent(uid)}`;
};

/** What the pending interaction needs (login vs. consent) + the requesting client. */
export async function getInteraction(uid: string): Promise<InteractionDetails> {
  const { data } = await client.get<InteractionDetails>(interactionUrl(uid));
  return data;
}

/** Complete the login prompt by handing the IdP a verified Firebase ID token. */
export async function submitInteractionLogin(
  uid: string,
  idToken: string,
): Promise<InteractionResult> {
  const { data } = await client.post<InteractionResult>(`${interactionUrl(uid)}/login`, {
    idToken,
  });
  return data;
}

/**
 * Grant the requested scopes (first-party partners auto-consent on the BE for the standard
 * scopes). `sharePlayers` carries the guardian's explicit choice for the `players` scope
 * (their children list — child data): `true` grants it, `false`/omitted completes login
 * without it (BE docs/tasks/019). Only send it when the interaction set
 * `requiresPlayersConsent`; otherwise let it default.
 */
export async function confirmInteraction(
  uid: string,
  sharePlayers?: boolean,
): Promise<InteractionResult> {
  const body = sharePlayers === undefined ? undefined : { sharePlayers };
  const { data } = await client.post<InteractionResult>(`${interactionUrl(uid)}/confirm`, body);
  return data;
}

/** Abort the interaction (user declined) — the RP receives `access_denied`. */
export async function abortInteraction(uid: string): Promise<InteractionResult> {
  const { data } = await client.post<InteractionResult>(`${interactionUrl(uid)}/abort`);
  return data;
}
